import { z } from 'zod';

import { AuthRoleWithAny } from '../role-utils';
import type { CreateOrgOptions, Org, UpdateOrgOptions } from './org';
import type { CreateSchoolOptions, School, UpdateSchoolOptions } from './school';
import type { CreateUserOptions, UpdateUserOptions, UserWithRolesAndProfiles } from './user';
import { Role } from './user-role';
import { generateCSVContent } from '../csv';

//#region Org chart

export type OrgData = Omit<Org, 'id'>;

/**
 * An org chart is a small tree of loaded entities that are made of: an org that
 * is the tree's root, zero or more schools that belong to the root org, and
 * zero or more users that each belong to at least one school.
 *
 * Org charts serve as a useful context object when synchronizing with an
 * external integration service, providing query methods for fast, in-memory
 * lookups.
 *
 * An org chart is always parameterized to an external ID type, but a class
 * implementing this protocol is also expected to provide additional methods
 * that are appropriate to the sync task it oversees.
 */
export interface OrgChart<ExternalId extends string> {
  /** A piece of org data, regardless of whether the org exists yet. */
  orgData: OrgData;

  /** An org ID can be missing, since the org may not be created yet. */
  orgId?: string;

  schools(): IterableIterator<School>;
  users(): IterableIterator<UserWithRolesAndProfiles>;

  findSchoolWithId(schoolId: string): School | undefined;
  findSchoolWithExternalId(externalId: ExternalId): School | undefined;
  findUserWithId(userId: string): UserWithRolesAndProfiles | undefined;
  findUserWithExternalId(externalId: ExternalId): UserWithRolesAndProfiles | undefined;
}

/**
 * A delta applier uses an org chart as context and statefully enacts the deltas
 * given to its methods, while processing a delta's vendor-specific information
 * if possible.
 *
 * It is responsible for remembering external ID references that are bound
 * through creation deltas, since such a feature is often useful for maintaining
 * the state of an {@link OrgChart} in the first place.
 */
export interface DeltaApplier<ExternalId extends string, DataAddition extends object> {
  applyOrgDelta(delta: OrgDelta<ExternalId, DataAddition>): Promise<Org>;
  applySchoolDelta(delta: SchoolDelta<ExternalId, DataAddition>): Promise<School>;
  applyUserDelta(delta: UserDelta<ExternalId, DataAddition>): Promise<UserWithRolesAndProfiles>;
}

//#endregion

//#region Delta

/**
 * A delta object is a structural diff for an internal entity. Because of how
 * some external services work, multiple external entities can contribute to a
 * single delta object. However, a delta object should be enactable with no
 * specific knowledge of an external service.
 *
 * Typically, a delta is created from three parts: an `OrgChart` representing a
 * slice of our internal reality, ingested data representing a slice of our
 * external service's reality, and an external entity within the ingested data
 * acting as the focal point of the delta.
 *
 * Delta creators are in charge of transforming data from external formats and
 * representations into internal ones. Once a delta is created, the data it
 * holds should not require additional transformations.
 *
 * Delta objects are designed to be inspectable by human eyes. As such, they
 * encode both the targeted entity's type as well as the delta kind.
 *
 * There are three kinds of delta objects: creations, deletions, and updates.
 */
export type Delta<ExternalId extends string, DataAddition extends object> =
  | CreationDelta<ExternalId, DataAddition>
  | DeletionDelta
  | UpdateDelta<ExternalId>;

export const deltaKindSchema = z.enum(['create', 'update', 'delete']);
export type DeltaKind = Delta<string, object>['kind'];
export type _CheckDeltaKind<T extends z.infer<typeof deltaKindSchema> = DeltaKind> = T;

/**
 * A creation delta represents an external entity that does not yet occur in an
 * org chart.
 *
 * Typically, initial imports are entirely made out of creation deltas. Due to
 * the hierarchal nature of entities and IDs, creation deltas further allow the
 * inclusion of external IDs in the following key positions:
 * - A creation delta can _set_ its external ID by specifying an `externalId`
 *   property. Once the entity it describes is created successfully, its
 *   internal ID is added to the delta applier's reference table.
 * - A creation delta can _get_ an external ID in a position that normally takes
 *   an internal ID. Instead of supplying a string, it can supply an object of
 *   type `{externalId: ExternalId}`. The delta applier will instead look up its
 *   reference table using the given external ID. If an internal ID was found,
 *   that is used. If it could not be found, an error is thrown.
 *
 * @example
 * ```
 * // This delta will associate its eventual org's ID with the external ID 'abc'
 * {
 *   delta: 'org',
 *   kind: 'create',
 *   externalId: 'abc',
 *   data: { name: 'ABC County', kind: 'district' },
 * }
 * // This delta uses the previously associated org's external ID 'abc', and it
 * // too will associate its eventual school's ID with the external ID 'defghi'.
 * {
 *   delta: 'school',
 *   kind: 'create',
 *   externalId: 'defghi',
 *   data: { name: 'Defghi School', orgId: { externalId: 'abc' } },
 * }
 * // This delta uses the previously associated school's external ID 'defghi'.
 * {
 *   delta: 'user',
 *   kind: 'create',
 *   data: { ... },
 *   roleDeltas: [{
 *     {
 *       delta: 'user_role',
 *       kind: 'create',
 *       data: { role: 'student', schoolId: { externalId: 'defghi' } },
 *     }
 *   }],
 * }
 * ```
 */
export type CreationDelta<ExternalId extends string, DataAddition extends object> =
  | OrgCreationDelta<ExternalId, DataAddition>
  | SchoolCreationDelta<ExternalId, DataAddition>
  | UserCreationDelta<ExternalId, DataAddition>;

/**
 * A deletion delta represents an internal entity that is linked to an external
 * service but no longer exists in that service.
 *
 * While its name is a "deletion" delta, we usually deactivate an entity rather
 * than destroy it out right.
 */
export type DeletionDelta = SchoolDeletionDelta | UserDeletionDelta;

/**
 * An update delta represents how much an external entity has changed compared
 * to the internal entity it is linked to.
 *
 * Update deltas are "slim": they only carry data that has changed. If an
 * external entity has not changed when compared to its internal equivalent, it
 * is natural to create no update delta for it.
 */
export type UpdateDelta<ExternalId extends string> = OrgUpdateDelta | SchoolUpdateDelta | UserUpdateDelta<ExternalId>;

/**
 * Represents changes to an org. Because an org forms the basis of a sync
 * operation, there is no org deletion delta.
 */
export type OrgDelta<ExternalId extends string, DataAddition extends object> =
  | OrgCreationDelta<ExternalId, DataAddition>
  | OrgUpdateDelta;

export interface OrgCreationDelta<ExternalId extends string, DataAddition extends object> {
  delta: 'org';
  kind: 'create';
  externalId?: ExternalId;
  data: CreateOrgOptions & DataAddition;
}

export interface OrgUpdateDelta {
  delta: 'org';
  kind: 'update';
  id: string;
  data: UpdateOrgOptions;
}

/**
 * Represents changes to a school.
 */
export type SchoolDelta<ExternalId extends string, DataAddition extends object> =
  | SchoolCreationDelta<ExternalId, DataAddition>
  | SchoolDeletionDelta
  | SchoolUpdateDelta;

export type SchoolCreationDeltaData<ExternalId extends string> = Omit<CreateSchoolOptions, 'orgId'> & {
  orgId: string | { externalId: ExternalId };
};

export interface SchoolCreationDelta<ExternalId extends string, DataAddition extends object> {
  delta: 'school';
  kind: 'create';
  externalId?: ExternalId;
  data: SchoolCreationDeltaData<ExternalId> & DataAddition;
}

/** A school is typically never actually destroyed, but deactivated. */
export interface SchoolDeletionDelta {
  delta: 'school';
  kind: 'delete';
  id: string;
}

export interface SchoolUpdateDelta {
  delta: 'school';
  kind: 'update';
  id: string;
  data: UpdateSchoolOptions;
}

/**
 * Represents changes to a user.
 */
export type UserDelta<ExternalId extends string, DataAddition extends object> =
  | UserCreationDelta<ExternalId, DataAddition>
  | UserDeletionDelta
  | UserUpdateDelta<ExternalId>;

export interface UserCreationDelta<ExternalId extends string, DataAddition extends object> {
  delta: 'user';
  kind: 'create';
  externalId?: ExternalId;
  data: CreateUserOptions & DataAddition;
  roleDeltas: UserRoleCreationDelta<ExternalId>[];
}

/** A user is typically never actually destroyed, but deactivated. */
export interface UserDeletionDelta {
  delta: 'user';
  kind: 'delete';
  id: string;
}

/**
 * Because user roles can be added and revoked over time, user roles have their
 * own delta type.
 */
export interface UserUpdateDelta<ExternalId extends string> {
  delta: 'user';
  kind: 'update';
  id: string;
  data: UserUpdateData;
  roleDeltas: UserRoleDelta<ExternalId>[];
}

/**
 * A piece of user update data only models the basic fields of a user. User
 * roles are modeled separately as their own delta type.
 */
export type UserUpdateData = Partial<Omit<UpdateUserOptions, 'assignRoles'>>;

/**
 * A user role can be created or deleted. There is currently no valid scenario
 * in which a user role changes one of its fields.
 */
export type UserRoleDelta<ExternalId extends string> = UserRoleCreationDelta<ExternalId> | UserRoleDeletionDelta;

export interface UserRoleCreationDelta<ExternalId extends string> {
  delta: 'user_role';
  kind: 'create';
  data: UserRoleData<ExternalId>;
}

/**
 * A user role deletion delta can match more than one user role if the criteria
 * is loose enough.
 *
 * User roles are typically never deleted, only deactivated.
 */
export interface UserRoleDeletionDelta {
  delta: 'user_role';
  kind: 'delete';
  /**
   * A partial match against a user role to delete.
   */
  match: AuthRoleWithAny;
}

/**
 * The identity of a user role is determined by only the name and the school ID.
 */
export interface UserRoleData<ExternalId extends string> {
  role: Role;
  schoolId: string | { externalId: ExternalId } | null;
}

//#endregion

export function csvForDeltas<ExternalId extends string, DataAddition extends object>(
  deltas: Array<Delta<ExternalId, DataAddition>>,
): string {
  return generateCSVContent(deltas, [
    {
      id: 'Action',
      getValue(delta) {
        switch (delta.kind) {
          case 'create':
            return 'Creation';
          case 'update':
            return 'Update';
          case 'delete':
            return 'Deletion';
        }
      },
    },
    {
      id: 'Entity type',
      getValue(delta) {
        switch (delta.delta) {
          case 'org':
            return 'District';
          case 'school':
            return 'School';
          case 'user':
            return 'User';
        }
      },
    },
    {
      id: 'Internal ID',
      getValue(delta) {
        switch (delta.kind) {
          case 'create':
            return null;
          case 'update': // fallthrough
          case 'delete':
            return delta.id;
        }
      },
    },
    {
      id: 'External ID',
      getValue(delta) {
        switch (delta.kind) {
          case 'create':
            return delta.externalId ?? null;
          case 'update':
            return delta.data.edlinkId ?? null;
          case 'delete':
            return null;
        }
      },
    },
    {
      id: 'Context',
      getValue(delta) {
        switch (delta.kind) {
          case 'create': // fallthrough
          case 'update':
            return JSON.stringify(delta.data);
          case 'delete':
            return null;
        }
      },
    },
  ]);
}
